// Файл ImageViewer.js

/**
 * Файл реализует логику модуля «ImageViewer» в разделе «DocumentAttachments» интерфейса.
 * В нём собраны связанные обработчики и вспомогательные операции для этой части приложения.
 * Код взаимодействует с соседними компонентами и передаёт им подготовленные данные без дублирования общей логики.
 * Такое разделение упрощает сопровождение, проверку поведения и дальнейшее расширение функциональности.
 */

import { useEffect, useState } from 'react';

import { use_image_pan_zoom } from './use_image_pan_zoom';


const ImageViewer = ({ blob, fileName }) => {
    const [url, set_url] = useState('');
    const viewer = use_image_pan_zoom();

    useEffect(() => {
        if (!blob) {
            set_url('');
            return undefined;
        }

        const object_url = URL.createObjectURL(blob);
        set_url(object_url);

        return () => URL.revokeObjectURL(object_url);
    }, [blob]);

    useEffect(() => {
        if (!url) {
            return undefined;
        }

        const observer = new ResizeObserver(() => viewer.fit_to_screen());

        if (viewer.canvas_ref.current) {
            observer.observe(viewer.canvas_ref.current);
        }

        return () => observer.disconnect();
    }, [url, viewer.canvas_ref, viewer.fit_to_screen]);

    const open_separately = () => {
        const opened_window = window.open(
            url,
            '_blank',
            'noopener,noreferrer',
        );

        if (opened_window) {
            opened_window.opener = null;
        }
    };

    if (!url) {
        return null;
    }

    return (
        <div className="image-viewer flex-column">
            <div className="image-viewer__toolbar flex-align-center">
                <button
                    type="button"
                    onClick={() => viewer.zoom_by(1 / 1.2)}
                    title="Уменьшить"
                >
                    −
                </button>
                <button
                    type="button"
                    onClick={() => viewer.zoom_by(1.2)}
                    title="Увеличить"
                >
                    +
                </button>
                <button
                    type="button"
                    className="image-viewer__wide-button"
                    onClick={viewer.fit_to_screen}
                    title="Вписать изображение в окно"
                >
                    Вписать
                </button>
                <button
                    type="button"
                    onClick={viewer.reset_one_to_one}
                    title="Показать в масштабе 1:1"
                >
                    1:1
                </button>
                <button
                    type="button"
                    className="image-viewer__wide-button"
                    onClick={open_separately}
                    title="Открыть изображение отдельно"
                >
                    Отделить
                </button>
                <span className="image-viewer__scale">
                    {Math.round(viewer.scale * 100)}%
                </span>
            </div>

            <div
                ref={viewer.canvas_ref}
                className="image-viewer__canvas grid-layout"
                onWheel={viewer.handle_wheel}
                onPointerDown={viewer.handle_pointer_down}
                onPointerMove={viewer.handle_pointer_move}
                onPointerUp={viewer.handle_pointer_end}
                onPointerCancel={viewer.handle_pointer_end}
                onDoubleClick={viewer.fit_to_screen}
            >
                <img
                    ref={viewer.image_ref}
                    src={url}
                    alt={fileName}
                    className="image-viewer__img"
                    draggable={false}
                    onLoad={viewer.fit_to_screen}
                    style={{
                        transform: (
                            `translate(${viewer.offset.x}px, `
                            + `${viewer.offset.y}px) `
                            + `scale(${viewer.scale})`
                        ),
                    }}
                />
            </div>
        </div>
    );
};


export default ImageViewer;
